import React from 'react';
import './Privacy.css';

const Privacy = () => {
  return (
    <div className="privacy-page">
      <div className="container">
        <div className="privacy-header">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: December 10, 2024</p>
        </div>

        <div className="privacy-content">
          <section className="privacy-section">
            <div className="section-number">1</div>
            <div className="section-content">
              <h2>Introduction</h2>
              <p>
                SyarTech ("we", "us", or "our") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and protect your information when you use our mobile application and services in the Arab Republic of Egypt. This policy complies with Egyptian Law No. 151 of 2020 on Personal Data Protection.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">2</div>
            <div className="section-content">
              <h2>Information We Collect</h2>
              <p>
                We collect the following types of information:
              </p>
              <p><strong>Personal Information:</strong></p>
              <ul>
                <li>Full name and contact details (phone number, email address)</li>
                <li>National ID number (for verification purposes only)</li>
                <li>Delivery addresses and location data</li>
                <li>Vehicle information (make, model, license plate)</li>
                <li>Payment information (card details, mobile wallet information)</li>
              </ul>
              <p><strong>Usage Information:</strong></p>
              <ul>
                <li>Service history and preferences</li>
                <li>App usage data and browsing behavior</li>
                <li>Device information (device type, operating system, unique identifiers)</li>
                <li>IP address and location data</li>
              </ul>
              <p><strong>Communication Data:</strong></p>
              <ul>
                <li>Customer service interactions</li>
                <li>Feedback and reviews</li>
                <li>Marketing preferences</li>
              </ul>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">3</div>
            <div className="section-content">
              <h2>How We Use Your Information</h2>
              <p>
                We use your personal information for the following purposes:
              </p>
              <ul>
                <li><strong>Service Delivery:</strong> To provide, maintain, and improve our car washing services</li>
                <li><strong>Account Management:</strong> To create and manage your account</li>
                <li><strong>Payment Processing:</strong> To process transactions securely</li>
                <li><strong>Communication:</strong> To send service updates, confirmations, and notifications</li>
                <li><strong>Customer Support:</strong> To respond to inquiries and resolve issues</li>
                <li><strong>Marketing:</strong> To send promotional offers (with your consent)</li>
                <li><strong>Analytics:</strong> To improve our services and user experience</li>
                <li><strong>Legal Compliance:</strong> To comply with Egyptian laws and regulations</li>
                <li><strong>Safety & Security:</strong> To prevent fraud and ensure platform security</li>
              </ul>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">4</div>
            <div className="section-content">
              <h2>Legal Basis for Processing</h2>
              <p>
                Under Egyptian Law No. 151 of 2020, we process your data based on:
              </p>
              <ul>
                <li><strong>Consent:</strong> You have given clear consent for specific purposes</li>
                <li><strong>Contract Performance:</strong> Processing is necessary to fulfill our service agreement</li>
                <li><strong>Legal Obligation:</strong> We must process data to comply with Egyptian law</li>
                <li><strong>Legitimate Interest:</strong> Processing is necessary for our legitimate business interests</li>
              </ul>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">5</div>
            <div className="section-content">
              <h2>Data Sharing and Disclosure</h2>
              <p>
                We may share your information with:
              </p>
              <ul>
                <li><strong>Service Technicians:</strong> Limited information needed to complete your service</li>
                <li><strong>Payment Processors:</strong> Secure payment processing partners</li>
                <li><strong>Technology Providers:</strong> Cloud hosting, analytics, and communication services</li>
                <li><strong>Business Partners:</strong> With your consent for joint promotions</li>
                <li><strong>Legal Authorities:</strong> When required by Egyptian law or court order</li>
              </ul>
              <p>
                We never sell your personal information to third parties. All partners are bound by strict confidentiality agreements.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">6</div>
            <div className="section-content">
              <h2>Data Storage and Security</h2>
              <p>
                We implement industry-standard security measures to protect your data:
              </p>
              <ul>
                <li>End-to-end encryption for sensitive data transmission</li>
                <li>Secure servers with regular security audits</li>
                <li>Access controls limiting employee data access</li>
                <li>Regular data backups and disaster recovery plans</li>
                <li>Two-factor authentication for account security</li>
              </ul>
              <p>
                Your data is primarily stored on servers located in Egypt or other secure locations that comply with Egyptian data protection standards. We retain your data only as long as necessary for service provision or as required by law.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">7</div>
            <div className="section-content">
              <h2>Your Privacy Rights</h2>
              <p>
                Under Egyptian Personal Data Protection Law, you have the right to:
              </p>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal data we hold</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal obligations)</li>
                <li><strong>Restriction:</strong> Limit how we use your data</li>
                <li><strong>Portability:</strong> Receive your data in a structured format</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent for marketing communications anytime</li>
                <li><strong>Complaint:</strong> File a complaint with Egyptian Data Protection Authority</li>
              </ul>
              <p>
                To exercise these rights, contact us through the app or our contact channels listed below.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">8</div>
            <div className="section-content">
              <h2>Data Retention</h2>
              <p>
                We retain your personal data for different periods depending on the purpose:
              </p>
              <ul>
                <li><strong>Account Information:</strong> As long as your account is active plus 1 year</li>
                <li><strong>Service Records:</strong> 5 years for business and tax purposes</li>
                <li><strong>Payment Data:</strong> 7 years as required by Egyptian tax law</li>
                <li><strong>Marketing Data:</strong> Until you opt-out or 2 years of inactivity</li>
                <li><strong>Legal Records:</strong> As required by Egyptian law (typically 10 years)</li>
              </ul>
              <p>
                After retention periods expire, we securely delete or anonymize your data.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">9</div>
            <div className="section-content">
              <h2>Location Data and Tracking</h2>
              <p>
                Our app uses location services to:
              </p>
              <ul>
                <li>Determine service availability in your area</li>
                <li>Navigate technicians to your location</li>
                <li>Provide accurate service estimates</li>
                <li>Improve service coverage and routing</li>
              </ul>
              <p>
                You can control location permissions through your device settings. Disabling location services may limit app functionality. We do not track your location when the app is not in use unless you enable this feature.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">10</div>
            <div className="section-content">
              <h2>Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and similar technologies to:
              </p>
              <ul>
                <li>Remember your preferences and settings</li>
                <li>Analyze app performance and user behavior</li>
                <li>Personalize your experience</li>
                <li>Deliver targeted advertisements (with consent)</li>
              </ul>
              <p>
                You can manage cookie preferences through your device or browser settings. Note that disabling cookies may affect app functionality.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">11</div>
            <div className="section-content">
              <h2>Children's Privacy</h2>
              <p>
                Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we discover that we have inadvertently collected data from a child, we will delete it immediately. Parents or guardians who believe their child has provided us with information should contact us immediately.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">12</div>
            <div className="section-content">
              <h2>Third-Party Services</h2>
              <p>
                Our app may contain links to third-party services (payment gateways, social media). We are not responsible for their privacy practices. We encourage you to review their privacy policies before providing any information.
              </p>
              <p>
                Third-party services we use include:
              </p>
              <ul>
                <li>Payment processors (e.g., Fawry, Paymob)</li>
                <li>Cloud services (AWS, Google Cloud)</li>
                <li>Analytics tools (Google Analytics, Firebase)</li>
                <li>Communication platforms (SMS providers, email services)</li>
              </ul>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">13</div>
            <div className="section-content">
              <h2>Marketing Communications</h2>
              <p>
                With your consent, we may send you:
              </p>
              <ul>
                <li>Promotional offers and discounts</li>
                <li>New service announcements</li>
                <li>Seasonal campaigns</li>
                <li>Loyalty program updates</li>
              </ul>
              <p>
                You can opt-out of marketing communications at any time by:
              </p>
              <ul>
                <li>Clicking "unsubscribe" in emails</li>
                <li>Adjusting notification settings in the app</li>
                <li>Contacting customer support</li>
              </ul>
              <p>
                Note: You cannot opt-out of transactional messages (service confirmations, account updates).
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">14</div>
            <div className="section-content">
              <h2>Data Breach Notification</h2>
              <p>
                In the event of a data breach that may affect your personal information, we will:
              </p>
              <ul>
                <li>Notify affected users within 72 hours</li>
                <li>Inform the Egyptian Data Protection Authority</li>
                <li>Provide details about the breach and affected data</li>
                <li>Outline steps taken to address the breach</li>
                <li>Advise on protective measures you can take</li>
              </ul>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">15</div>
            <div className="section-content">
              <h2>International Data Transfers</h2>
              <p>
                While we primarily store data within Egypt, some service providers may process data internationally. When transferring data outside Egypt, we ensure:
              </p>
              <ul>
                <li>Adequate protection equivalent to Egyptian standards</li>
                <li>Standard contractual clauses with foreign processors</li>
                <li>Compliance with Egyptian Data Protection Authority guidelines</li>
                <li>Your explicit consent when required</li>
              </ul>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">16</div>
            <div className="section-content">
              <h2>Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we make significant changes:
              </p>
              <ul>
                <li>We will notify you via app notification and email</li>
                <li>The "Last Updated" date will be revised</li>
                <li>We will seek your consent if required by law</li>
                <li>Continued use after notification constitutes acceptance</li>
              </ul>
              <p>
                We recommend reviewing this policy periodically to stay informed about how we protect your data.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <div className="section-number">17</div>
            <div className="section-content">
              <h2>Contact Information</h2>
              <p>
                For privacy-related questions, concerns, or to exercise your rights, contact us:
              </p>
              <ul>
                <li><strong>Data Protection Officer:</strong> Available through in-app support</li>
                <li><strong>Email:</strong> privacy@syartech.eg (through our contact form)</li>
                <li>Website: <a href="https://kamshka.com/contact" target="_blank" rel="noopener noreferrer">kamshka.com/contact</a></li>
                <li>Facebook: <a href="https://www.facebook.com/profile.php?id=61584276933010" target="_blank" rel="noopener noreferrer">SyarTech Egypt</a></li>
                <li>Instagram: <a href="https://www.instagram.com/car_washeg/" target="_blank" rel="noopener noreferrer">@car_washeg</a></li>
              </ul>
              <p>
                <strong>Egyptian Data Protection Authority:</strong><br />
                If you're not satisfied with our response, you may file a complaint with the Egyptian Data Protection Authority in accordance with Law No. 151 of 2020.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;